import React, { useState } from 'react';
import {
  Button,
  Combobox,
  Flex,
  Textarea,
  Tooltip,
} from '@servicetitan/anvil2';
import { Headline } from '@servicetitan/design-system';
import { TaskOutcome } from '@api/generated/graphql';
import styles from './LogOutcomeSection.module.css';
import strings from './strings';
import { inboxLocators } from '@locators/inbox';
import { getTestIdProps } from '@lib/testUtils';

export interface LogOutcomeSectionProps {
  liveAnswerOutcomeOptions: TaskOutcome[];
  noAnswerOutcomeOptions: TaskOutcome[];
  onSubmit: (outcome: TaskOutcome, notes?: string) => Promise<void>;
  isCallEnded: boolean;
}

interface CallOutcomeOption {
  text: string;
  value: TaskOutcome;
  // Numeric prefixes control group sort order; groupToString maps to display labels
  group: '1_no_answer' | '2_answered';
}

const GROUP_LABELS: Record<string, string> = {
  '1_no_answer': 'NO ANSWER',
  '2_answered': 'ANSWERED',
};

const formatEnumToDisplay = (enumValue: string): string =>
  enumValue
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

// CCP-39878: Temporarily hide the "Do Not Contact" outcome from manual logging
// per legal request. Backend still returns it in the option lists; filter it on
// the frontend so it can be restored without a backend change.
const HIDDEN_OUTCOMES: ReadonlySet<TaskOutcome> = new Set([
  TaskOutcome.DoNotContact,
]);

const buildOutcomeOptions = (
  noAnswerOptions: TaskOutcome[],
  liveAnswerOptions: TaskOutcome[],
): CallOutcomeOption[] => [
  ...noAnswerOptions
    .filter((value) => !HIDDEN_OUTCOMES.has(value))
    .map((value) => ({
      text: formatEnumToDisplay(value),
      value,
      group: '1_no_answer' as const,
    })),
  ...liveAnswerOptions
    .filter((value) => !HIDDEN_OUTCOMES.has(value))
    .map((value) => ({
      text: formatEnumToDisplay(value),
      value,
      group: '2_answered' as const,
    })),
];

export const LogOutcomeSection: React.FC<LogOutcomeSectionProps> = ({
  liveAnswerOutcomeOptions,
  noAnswerOutcomeOptions,
  onSubmit,
  isCallEnded,
}) => {
  const [callOutcome, setCallOutcome] = useState<CallOutcomeOption | null>(
    null,
  );
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const outcomeOptions = buildOutcomeOptions(
    noAnswerOutcomeOptions,
    liveAnswerOutcomeOptions,
  );

  const isSubmitEnabled = isCallEnded && !!callOutcome && !isSubmitting;

  const handleSubmitLogOutcome = async () => {
    if (!isSubmitEnabled) return;

    setIsSubmitting(true);
    try {
      await onSubmit(callOutcome.value, notes);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Flex direction='column' gap='6' className={styles.container}>
      <Headline size='medium' className={styles.heading}>
        {strings.labels.heading}
      </Headline>

      <Textarea
        label={strings.labels.taskNotes}
        placeholder={strings.placeholders.taskNotes}
        rows={4}
        value={notes}
        onChange={(_, data) => {
          if (typeof data?.value === 'string') {
            setNotes(data.value);
          }
        }}
        disabled={!isCallEnded || isSubmitting}
        disableResize
      />

      <div className={styles.comboboxContainer}>
        <Combobox.Select
          items={outcomeOptions}
          itemToString={(item) => item?.text ?? ''}
          onChange={setCallOutcome}
          selectedItem={callOutcome}
          disabled={!isCallEnded || isSubmitting}
          groupBy='group'
          groupToString={(groupValue) => GROUP_LABELS[groupValue] ?? null}
        >
          <Combobox.SelectTrigger
            label={strings.labels.callOutcome}
            required
            placeholder={strings.placeholders.callOutcome}
          />
          <Combobox.Content>
            {({ items }) => (
              <Combobox.List>
                {items.map((item, i) => (
                  <Combobox.Item key={item.value} item={item} index={i}>
                    {item.text}
                  </Combobox.Item>
                ))}
              </Combobox.List>
            )}
          </Combobox.Content>
        </Combobox.Select>
      </div>

      <Tooltip>
        <Tooltip.Trigger>
          <Button
            appearance='primary'
            size='medium'
            onClick={handleSubmitLogOutcome}
            disabled={!isSubmitEnabled}
            loading={isSubmitting}
            {...getTestIdProps(
              inboxLocators.taskList.speedToLeadLogOutcomeSubmit,
            )}
          >
            {strings.labels.submitButton}
          </Button>
        </Tooltip.Trigger>
        {!isSubmitEnabled && !isSubmitting && (
          <Tooltip.Content>
            {strings.labels.submitDisabledTooltip}
          </Tooltip.Content>
        )}
      </Tooltip>
    </Flex>
  );
};
